import { useCallback, useState } from 'react';
import i18n from '@/i18n/config';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem
} from '@heroui/react';
import { useAtom } from 'jotai';
import { addOrganizationMember } from '@/store';
import type { RoleType } from '@/components/shared/RoleChip';

interface AddMemberFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

interface MemberFormData {
  email: string;
  job_title: string;
  role: RoleType;
}

// Constants
const DEFAULT_FORM_DATA: MemberFormData = {
  email: '',
  job_title: '',
  role: 'VIEWER'
};

const ROLE_OPTIONS: Array<{ key: RoleType; label: string }> = [
  { key: 'ADMIN', label: i18n.t('enums.admin') },
  { key: 'EDITOR', label: i18n.t('copy.editor') },
  { key: 'VIEWER', label: i18n.t('copy.viewer') }
];

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export function AddMemberForm({ isOpen, onOpenChange, organizationId, onSuccess }: AddMemberFormProps) {
  const [formData, setFormData] = useState<MemberFormData>(DEFAULT_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, addOrganizationMemberAtom] = useAtom(addOrganizationMember);

  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      return;
    }

    try {
      setIsSubmitting(true);
      await addOrganizationMemberAtom({
        organizationId,
        email: trimmedEmail,
        role: formData.role
      });

      resetForm();
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, organizationId, onSuccess, onOpenChange, addOrganizationMemberAtom, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [onOpenChange, resetForm]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting && formData.email.trim() && isValidEmail(formData.email.trim())) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting, formData.email]
  );

  const isFormValid = formData.email.trim() && isValidEmail(formData.email.trim());

  const updateFormField = useCallback(<K extends keyof MemberFormData>(field: K, value: MemberFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRoleChange = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      const role = Array.from(keys as Set<string>)[0] as RoleType;
      if (role) {
        updateFormField('role', role);
      }
    },
    [updateFormField]
  );

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Member</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Input
                  label={i18n.t('auth.email')}
                  placeholder={i18n.t('copy.enter_member_email')}
                  type="email"
                  value={formData.email}
                  onValueChange={(value) => updateFormField('email', value)}
                  isRequired
                  errorMessage={
                    formData.email.trim() && !isValidEmail(formData.email.trim())
                      ? 'Please enter a valid email address'
                      : undefined
                  }
                  isInvalid={formData.email.trim() !== '' && !isValidEmail(formData.email.trim())}
                />
                <Input
                  label={i18n.t('copy.job_title')}
                  placeholder={i18n.t('copy.enter_job_title_optional')}
                  value={formData.job_title}
                  onValueChange={(value) => updateFormField('job_title', value)}
                />
                <Select
                  label={i18n.t('filters.role')}
                  placeholder={i18n.t('copy.select_role')}
                  selectedKeys={new Set([formData.role])}
                  onSelectionChange={handleRoleChange}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit} isDisabled={!isFormValid}>
                Add Member
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
